'use client';

import { keepPreviousData, type QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useUserContext } from '@module/mdrt-org/shared/components/providers/user-context';
import { BADGE_REFRESH_MESSAGE_TYPE, CLOSE_ALL_NOTIFICATIONS_MESSAGE_TYPE, REQUEST_BADGE_REFRESH_MESSAGE_TYPE, UNREAD_COUNT_POLL_INTERVAL_MS } from '@module/mdrt-org/shared/utils/constants/notification-badge';
import { WIDGET_NOTIFICATION_COUNT } from '@module/mdrt-org/shared/utils/constants/notifications-widget';
import { OrgSharedDataTag } from '@module/mdrt-org/shared/utils/data/constants/tags';
import { type NotificationsUICmsContent } from '@module/mdrt-org/shared/utils/data/get-notifications-widget-cms-content';
import { getUserNotifications, type UserNotification, type UserNotificationsDTO } from '@module/mdrt-org/shared/utils/data/get-user-notifications';
import { applyStoredUnreadCountFallback, applyUnreadCount, clearAppBadge, flushAppBadge, repaintBadgeOnVisibility } from '@module/mdrt-org/shared/utils/helpers/app-badge';
type NotificationsValue = {
  allNotificationsAreRead: boolean;
  cmsContent: NotificationsUICmsContent;
  isError: boolean;
  isFetching: boolean;
  isLoading: boolean;
  notifications: UserNotification[];
  notificationsQueryKey: QueryKey;
  refreshNotifications: () => void;
  showMoreNotifications: () => void;
  totalNotificationsCount: number;
};
const Notifications = createContext<NotificationsValue>({} as NotificationsValue);

// Asks the active Service Worker to close all displayed (tray) notifications. On
// Android the installed-PWA launcher icon dot is driven by tray notifications (the
// Badging API is unsupported there), so once the user has read everything this is the
// only way to clear the icon dot. On desktop/iOS the badge is cleared via the Badging
// API separately; this call is a harmless best-effort no-op there. Best-effort.
const requestCloseAllNotifications = async (): Promise<void> => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.ready;

    // The second argument is an (empty) transfer list — `ServiceWorker.postMessage`
    // has no `targetOrigin` (that's only for `window.postMessage`); it's passed to
    // satisfy the lint rule without changing behavior.
    registration.active?.postMessage({
      type: CLOSE_ALL_NOTIFICATIONS_MESSAGE_TYPE
    }, []);
  } catch {
    // best-effort — clearing the tray is a progressive enhancement
  }
};

// Asks the active Service Worker to (re)apply the app icon badge from the worker
// context. On Edge the taskbar badge only repaints on a navigation when set from the
// window/document context, so foreground updates (60s poll, reload, mark-as-read,
// opening the dropdown) delegate to the SW — the same context the push path uses —
// which repaints reliably without a navigation. The current unread `count` is passed
// so the SW can apply it directly without an extra unread-count fetch. Best-effort.
const requestServiceWorkerBadgeRefresh = async (count?: number): Promise<void> => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    registration.active?.postMessage({
      count,
      type: REQUEST_BADGE_REFRESH_MESSAGE_TYPE
    }, []);
  } catch {
    // best-effort — badge refresh is a progressive enhancement
  }
};
export const NotificationsProvider = ({
  children,
  cmsContent
}: PropsWithChildren<{
  cmsContent: NotificationsUICmsContent;
}>) => {
  const {
    accessToken,
    personId
  } = useUserContext();
  const queryClient = useQueryClient();

  // Tracks whether the user was authenticated on the previous render so the badge can
  // be cleared on the authenticated -> unauthenticated transition (logout / session
  // loss), including the session-error logout path that doesn't go through the button.
  const wasAuthenticatedRef = useRef(false);

  // Last unread count actually painted to the badge. Used to only run the Edge
  // repaint nudge (`flushAppBadge`, which flickers) when the count truly changes,
  // never on the idle 60s re-apply of the same value.
  const lastBadgeCountRef = useRef<null | number>(null);

  // Guards the visibility/focus repaint below against re-entrancy: restoring the PWA
  // fires both `visibilitychange` and `focus` almost simultaneously, so without this
  // the paired events would launch two overlapping `flushAppBadge` cycles (each clears
  // then re-sets the badge) and interleave into a worse flicker. Set synchronously when
  // a repaint starts and cleared when it settles, so the pair coalesces into a single
  // flush while a later genuinely new focus transition still repaints.
  const isRepaintInFlightRef = useRef(false);

  // Snapshot of the latest badge-relevant state, read by the visibility/focus repaint
  // handler below. Kept in a ref so that handler never re-subscribes on every count
  // change and never closes over a stale value.
  const badgeStateRef = useRef({
    isAuthenticated: false,
    isErrored: false,
    unreadCount: 0
  });
  const [loadedNotificationsCount, setLoadedNotificationsCount] = useState(WIDGET_NOTIFICATION_COUNT);
  const showMoreNotifications = () => {
    setLoadedNotificationsCount(previousState => previousState + WIDGET_NOTIFICATION_COUNT);
  };

  // Refetches the unread count / notifications on demand. Used when the dropdown opens
  // so the badge re-syncs to the true current unread count (and, on Edge, repaints via
  // the Service Worker once the fresh data flows through the badge effect above).
  const refreshNotifications = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [OrgSharedDataTag.GET_USER_NOTIFICATIONS]
    });
  }, [queryClient]);
  const notificationsQueryKey: QueryKey = useMemo(() => [OrgSharedDataTag.GET_USER_NOTIFICATIONS, loadedNotificationsCount], [loadedNotificationsCount]);
  const {
    data: notificationsData,
    isError,
    isFetching,
    isLoading
  } = useQuery<UserNotificationsDTO>({
    enabled: Boolean(accessToken && personId),
    placeholderData: keepPreviousData,
    queryFn: () => getUserNotifications({
      accessToken,
      itemsCount: loadedNotificationsCount,
      personId
    }),
    queryKey: notificationsQueryKey,
    // Keep the unread count fresh while the app is open. The global QueryClient
    // disables focus/mount refetch and sets a 60s staleTime, so without these
    // overrides the badge would go stale on platforms with no Periodic Background
    // Sync (e.g. iOS PWAs). `refetchInterval` only polls while the document is
    // visible (`refetchIntervalInBackground` defaults to false).
    refetchInterval: UNREAD_COUNT_POLL_INTERVAL_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0
  });
  const notifications = useMemo(() => notificationsData?.notifications ?? [], [notificationsData]);
  const totalNotificationsCount = notificationsData?.totalCount ?? 0;
  const unreadNotificationsCount = notificationsData?.unreadCount ?? 0;

  // Single source of truth for "unread" across the header bell indicator, the
  // mark-all-as-read button and the app icon badge: the API's unread count (kept
  // current by optimistic updates on mark-as-read). Previously the bell derived this
  // from the loaded page only (`notifications.every(isRead)`), which could diverge
  // from the badge when unread items were paginated out of the loaded set.
  const allNotificationsAreRead = unreadNotificationsCount === 0;

  // Keep the installed PWA icon badge in sync with the unread count whenever the
  // notifications query updates (app/site opened, mark-as-read, etc.). On a failed
  // fetch, fall back to the last-known stored count so the badge is never wrongly
  // cleared.
  useEffect(() => {
    // Snapshot the latest state for the visibility/focus repaint handler below.
    badgeStateRef.current = {
      isAuthenticated: Boolean(accessToken && personId),
      isErrored: isError,
      unreadCount: unreadNotificationsCount
    };
    if (!(accessToken && personId)) {
      // Authenticated -> unauthenticated transition (logout / session loss): clear the
      // badge and its stored count so a signed-out session can never keep a stale
      // value. Guarded by the ref so we don't clear on the initial unauthenticated
      // render (nothing to clear yet).
      if (wasAuthenticatedRef.current) {
        wasAuthenticatedRef.current = false;
        lastBadgeCountRef.current = null;
        void clearAppBadge();
        void requestServiceWorkerBadgeRefresh(0);
      }
      return;
    }
    wasAuthenticatedRef.current = true;
    if (isError) {
      void applyStoredUnreadCountFallback();
      void requestServiceWorkerBadgeRefresh();
      return;
    }
    if (notificationsData) {
      // Only run the Edge repaint nudge when the count actually changed (it briefly
      // clears then re-sets the badge, causing a flicker) — otherwise just re-apply
      // the same value silently so the 60s poll doesn't flicker the badge every tick.
      const hasChanged = unreadNotificationsCount !== lastBadgeCountRef.current;
      lastBadgeCountRef.current = unreadNotificationsCount;
      if (hasChanged) {
        // Edge only flushes the taskbar badge on a navigation; `flushAppBadge` toggles
        // the badge off/on to force a repaint without a reload.
        void flushAppBadge(unreadNotificationsCount);
      } else {
        void applyUnreadCount(unreadNotificationsCount);
      }

      // Also delegate to the Service Worker (worker context) as a fallback repaint path.
      void requestServiceWorkerBadgeRefresh(unreadNotificationsCount);

      // On Android the launcher icon dot is driven by tray notifications, not the
      // Badging API. Once everything is read, ask the Service Worker to close the
      // tray notifications so the dot clears too. The badge is already cleared on
      // desktop/iOS above; this covers Android.
      if (unreadNotificationsCount === 0) {
        void requestCloseAllNotifications();
      }
    }
  }, [accessToken, isError, notificationsData, personId, unreadNotificationsCount]);

  // Refetch the unread count immediately when the Service Worker signals an incoming
  // push. This keeps the in-app bell indicator and the icon badge fresh while the app
  // is open on platforms without Periodic Background Sync (e.g. iOS PWAs), rather than
  // waiting for the next poll.
  useEffect(() => {
    if (!(accessToken && personId)) return undefined;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === BADGE_REFRESH_MESSAGE_TYPE) {
        void queryClient.invalidateQueries({
          queryKey: [OrgSharedDataTag.GET_USER_NOTIFICATIONS]
        });
      }
    };
    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [accessToken, personId, queryClient]);

  // Force a badge repaint whenever the app regains visibility/focus. On Edge/Windows
  // the taskbar badge overlay is only committed to the OS shell on a navigation; when
  // the installed PWA is minimized and restored, Edge repaints the overlay from its
  // last-navigation snapshot, discarding any live in-app increment set since (the
  // badge appears to revert to an older count). `refetchOnWindowFocus` refreshes the
  // number, but when the fetched count is unchanged the sync effect above only does a
  // plain `applyUnreadCount`, which does not force an Edge repaint. So here we always
  // run the repaint-forcing `flushAppBadge` (plus the SW repaint path) on the
  // visibility/focus transition, regardless of whether the count changed, so the
  // taskbar overlay is repainted to the live value. Reads current state from a ref so
  // this listener never re-subscribes on every count change. A re-entrancy guard
  // coalesces the paired `visibilitychange`/`focus` events into a single flush.
  // Best-effort.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    // The async worker only ever clears the guard (never reads it), keeping the
    // read+set of the flag fully synchronous in the handler below.
    const runRepaint = async () => {
      try {
        const state = badgeStateRef.current;
        const didFlush = await repaintBadgeOnVisibility(state);
        if (didFlush) {
          void requestServiceWorkerBadgeRefresh(state.unreadCount);
        }
      } finally {
        isRepaintInFlightRef.current = false;
      }
    };

    // Coalesce the paired visibilitychange/focus events (and any overlap while the
    // ~60ms flush nudge is in flight) into a single repaint. The guard is read and set
    // synchronously here so nothing can slip in before the flag is raised.
    const handleRepaint = () => {
      if (document.visibilityState !== 'visible') return;
      if (isRepaintInFlightRef.current) return;
      isRepaintInFlightRef.current = true;
      void runRepaint();
    };
    document.addEventListener('visibilitychange', handleRepaint);
    window.addEventListener('focus', handleRepaint);
    return () => {
      document.removeEventListener('visibilitychange', handleRepaint);
      window.removeEventListener('focus', handleRepaint);
    };
  }, []);
  const value = useMemo(() => ({
    allNotificationsAreRead,
    cmsContent,
    isError,
    isFetching,
    isLoading,
    notifications: notifications ?? [],
    notificationsQueryKey,
    refreshNotifications,
    showMoreNotifications,
    totalNotificationsCount
  }), [allNotificationsAreRead, cmsContent, isError, isFetching, isLoading, notifications, notificationsQueryKey, refreshNotifications, totalNotificationsCount]);
  return <Notifications.Provider value={value}>{children}</Notifications.Provider>;
};
export const useNotifications = () => {
  const value = useContext(Notifications);
  if (!value) {
    throw new Error(`${useNotifications.name} must be used inside of ${NotificationsProvider.name}`);
  }
  return value;
};